import React, { useState, useRef, useCallback } from 'react';
import { Share2, Download, X, Copy, Check } from 'lucide-react';

const CARD_W = 600;
const CARD_H = 400;

const drawCard = (canvas, results, meta) => {
  const ctx = canvas.getContext('2d');
  canvas.width = CARD_W * 2;
  canvas.height = CARD_H * 2;
  ctx.scale(2, 2);

  // Background
  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Border
  ctx.strokeStyle = '#27272a';
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, CARD_W - 1, CARD_H - 1);

  // Top accent line
  const grad = ctx.createLinearGradient(0, 0, CARD_W, 0);
  grad.addColorStop(0, '#E63946');
  grad.addColorStop(1, '#ff6b35');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, CARD_W, 3);

  // Logo text
  ctx.font = 'bold 14px "Bebas Neue", Impact, sans-serif';
  ctx.fillStyle = '#E63946';
  ctx.textAlign = 'left';
  ctx.letterSpacing = '2px';
  ctx.fillText('NINJATHLETE', 24, 32);

  // Date
  ctx.font = '10px "Manrope", Arial, sans-serif';
  ctx.fillStyle = '#52525b';
  ctx.textAlign = 'right';
  ctx.fillText(new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), CARD_W - 24, 32);

  // Title
  ctx.font = 'bold 26px "Bebas Neue", Impact, sans-serif';
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'left';
  ctx.fillText('MY TRAINING STATS', 24, 72);

  // Subtitle line
  ctx.fillStyle = '#3f3f46';
  ctx.fillRect(24, 82, CARD_W - 48, 1);

  // Draw stats grid
  const entries = Object.entries(results);
  const cols = Math.min(entries.length, 3);
  const colW = (CARD_W - 48) / cols;
  let y = 110;
  let col = 0;

  entries.forEach(([key, data], i) => {
    const m = meta[key];
    if (!m || !data) return;

    const x = 24 + col * colW;

    // Label
    ctx.font = '9px "Manrope", Arial, sans-serif';
    ctx.fillStyle = '#71717a';
    ctx.textAlign = 'left';
    ctx.fillText(m.label.toUpperCase(), x, y);

    // Value
    ctx.font = 'bold 32px "Bebas Neue", Impact, sans-serif';
    ctx.fillStyle = m.color;
    ctx.fillText(String(data.value), x, y + 32);

    // Unit
    ctx.font = '11px "Manrope", Arial, sans-serif';
    ctx.fillStyle = '#52525b';
    const valWidth = ctx.measureText(String(data.value)).width;
    ctx.fillText(m.unit, x + valWidth + 4, y + 32);

    // Detail
    ctx.font = '10px "Manrope", Arial, sans-serif';
    ctx.fillStyle = '#3f3f46';
    const detail = data.label ? data.label.substring(0, 40) : '';
    ctx.fillText(detail, x, y + 48);

    col++;
    if (col >= cols) {
      col = 0;
      y += 80;
    }
  });

  // Bottom section
  const footerY = CARD_H - 40;
  ctx.fillStyle = '#18181b';
  ctx.fillRect(0, footerY - 10, CARD_W, 50);
  ctx.fillStyle = '#27272a';
  ctx.fillRect(0, footerY - 10, CARD_W, 1);

  ctx.font = '10px "Manrope", Arial, sans-serif';
  ctx.fillStyle = '#52525b';
  ctx.textAlign = 'left';
  ctx.fillText('ninjathlete.com/tools', 24, footerY + 12);

  ctx.textAlign = 'right';
  ctx.fillStyle = '#00F0FF';
  ctx.fillText('Track your progress free', CARD_W - 24, footerY + 12);
};

export const ShareStatsCard = ({ results, meta, onClose }) => {
  const canvasRef = useRef(null);
  const [generated, setGenerated] = useState(false);
  const [copied, setCopied] = useState(false);

  const generate = useCallback(() => {
    if (canvasRef.current) {
      drawCard(canvasRef.current, results, meta);
      setGenerated(true);
    }
  }, [results, meta]);

  const handleDownload = () => {
    if (!canvasRef.current) return;
    const link = document.createElement('a');
    link.download = 'ninjathlete-stats.png';
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
  };

  const handleCopyImage = async () => {
    if (!canvasRef.current) return;
    try {
      const blob = await new Promise(resolve => canvasRef.current.toBlob(resolve, 'image/png'));
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      handleDownload();
    }
  };

  const handleShare = async () => {
    if (!canvasRef.current) return;
    try {
      const blob = await new Promise(resolve => canvasRef.current.toBlob(resolve, 'image/png'));
      const file = new File([blob], 'ninjathlete-stats.png', { type: 'image/png' });
      if (navigator.share) {
        await navigator.share({
          title: 'My Training Stats - NinjAthlete',
          text: 'Check out my fitness stats!',
          url: 'https://ninjathlete.com/tools',
          files: [file],
        });
      } else {
        handleCopyImage();
      }
    } catch {
      handleDownload();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="share-stats-modal">
      <div className="bg-zinc-900 border border-zinc-800 max-w-[640px] w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h3 className="text-white text-lg font-heading">Share My Stats</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors" data-testid="close-share-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Canvas */}
        <div className="p-4">
          <canvas
            ref={canvasRef}
            style={{ width: CARD_W, height: CARD_H, display: generated ? 'block' : 'none' }}
            className="w-full border border-zinc-800 mx-auto"
            data-testid="stats-canvas"
          />
          {!generated && (
            <div className="flex flex-col items-center justify-center h-[200px]">
              <button
                onClick={generate}
                className="px-6 py-3 bg-gradient-to-r from-[#E63946] to-[#ff6b35] text-white font-heading uppercase tracking-wider text-sm hover:from-[#d62f3c] hover:to-[#e5612e] transition-all"
                data-testid="generate-stats-btn"
              >
                Generate My Stats Card
              </button>
              <p className="text-zinc-600 text-xs mt-3">Creates a shareable image of your saved results</p>
            </div>
          )}
        </div>

        {/* Actions */}
        {generated && (
          <div className="flex gap-2 p-4 pt-0">
            <button onClick={handleDownload} className="flex-1 flex items-center justify-center gap-2 py-3 bg-zinc-800 text-white text-sm hover:bg-zinc-700 transition-colors" data-testid="download-stats-btn">
              <Download className="w-4 h-4" /> Download
            </button>
            <button onClick={handleCopyImage} className="flex-1 flex items-center justify-center gap-2 py-3 bg-zinc-800 text-white text-sm hover:bg-zinc-700 transition-colors" data-testid="copy-stats-btn">
              {copied ? <Check className="w-4 h-4 text-[#22c55e]" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Image'}
            </button>
            <button onClick={handleShare} className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#E63946] text-white text-sm hover:bg-[#d62f3c] transition-colors" data-testid="share-native-btn">
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
